import { FC, SyntheticEvent, useEffect, useState } from 'react'
import { Modal, Pagination } from 'is24-corecss'
import { Trans, useTranslation } from 'react-i18next'
import './TooltipModal.css'

export interface TooltipModalProps {
  isOpen: boolean
  tooltipKey: string
  namespace?: string
  onClose: () => void
}

export const TooltipModal: FC<TooltipModalProps> = ({ isOpen, tooltipKey, namespace, onClose }: TooltipModalProps) => {
  const { t, i18n } = useTranslation(namespace)
  const tipsKey = `${tooltipKey}.tips`
  const translatedTips = t(tipsKey, { returnObjects: true })
  const tips = Array.isArray(translatedTips)
    ? translatedTips.filter((tip): tip is string => typeof tip === 'string')
    : []
  const FIRST_TIP_PAGE = 1
  const [activeTipPage, setActiveTipPage] = useState(FIRST_TIP_PAGE)
  const activeTipPageForTips = Math.min(activeTipPage, tips.length || FIRST_TIP_PAGE)
  const activeTip = tips[activeTipPageForTips - FIRST_TIP_PAGE] ?? ''
  const hasTips = i18n.exists(tipsKey, { ns: namespace }) && tips.length > 0

  useEffect(() => {
    setActiveTipPage(FIRST_TIP_PAGE)
  }, [isOpen, tooltipKey, namespace])

  const updateTipPage = (tipPage: number) => {
    setActiveTipPage(Math.min(Math.max(tipPage, FIRST_TIP_PAGE), tips.length))
  }

  const handleClickPage = (event: SyntheticEvent, page?: number | null) => {
    const eventPage = Number((event.currentTarget as HTMLElement).textContent)
    const tipPage = page ?? eventPage

    if (Number.isFinite(tipPage)) {
      updateTipPage(tipPage)
    }
  }

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose}>
      <Modal.Backdrop>
        <Modal.Surface width='unset' className='tooltip-modal'>
          <Modal.Header>
            <Modal.Title>{t(`${tooltipKey}.title`)}</Modal.Title>
            <Modal.CloseButton aria-label='close' />
          </Modal.Header>
          <Modal.Body>
            {hasTips && (
              <>
                <Trans i18nKey={activeTip} />
                {tips.length > 1 && (
                  <div className='tooltip-modal-pagination padding-top-m'>
                    <Pagination
                      pages={tips.length}
                      activePage={activeTipPageForTips}
                      showEllipsis={false}
                      onClickPage={handleClickPage}
                      onClickPrev={() => updateTipPage(activeTipPageForTips - 1)}
                      onClickNext={() => updateTipPage(activeTipPageForTips + 1)}
                    />
                  </div>
                )}
              </>
            )}
          </Modal.Body>
        </Modal.Surface>
      </Modal.Backdrop>
    </Modal.Root>
  )
}
